import { useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { Input, Select } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { RoomCategory } from "../api/types";

const DEFAULT_START_COLOR = "#6C5CE7";
const DEFAULT_END_COLOR = "#A29BFE";

// Round 37 — the real catalog behind the Android app's Room Settings
// Category picker (GET /rooms/categories via the Worker, room-profile.js),
// and the gradient the Home feed's live-room card renders for whichever
// category a room has selected (live_rooms_public, migration 0084).
// Nothing here is hardcoded in the app.
export function RoomCategoriesPage() {
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } = usePagedList<RoomCategory>("/admin/room-categories");
  const [editing, setEditing] = useState<Partial<RoomCategory> | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  async function save() {
    if (!editing?.name) {
      showToast("Name is required", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      const body = {
        name: editing.name,
        gradient_start_color: editing.gradient_start_color || null,
        gradient_end_color: editing.gradient_end_color || null,
        sort_order: editing.sort_order ?? 0,
        is_active: editing.is_active ?? true,
        // Round 38 (item 7) — independent of is_active: only controls
        // whether this category appears as a Home tab.
        show_on_home: editing.show_on_home ?? true,
      };
      if (editing.id) {
        await apiRequest(`/admin/room-categories/${editing.id}`, { method: "PATCH", body });
      } else {
        await apiRequest("/admin/room-categories", { method: "POST", body });
      }
      showToast("Saved", "success");
      reload();
      setEditing(null);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save category", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function toggleActive(row: RoomCategory) {
    try {
      await apiRequest(`/admin/room-categories/${row.id}`, { method: "PATCH", body: { is_active: !row.is_active } });
      showToast(row.is_active ? "Deactivated" : "Activated", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not update category", "error");
    }
  }

  const columns: Column<RoomCategory>[] = [
    {
      header: "Category",
      render: (row) => (
        <div className="flex items-center gap-2">
          <div
            className="h-10 w-10 rounded-lg"
            style={{
              background: `linear-gradient(135deg, ${row.gradient_start_color || "#CCCCCC"}, ${row.gradient_end_color || "#CCCCCC"})`,
            }}
          />
          <p className="font-medium text-ink">{row.name}</p>
        </div>
      ),
    },
    { header: "Sort Order", render: (row) => row.sort_order },
    { header: "Status", render: (row) => (row.is_active ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Inactive</Badge>) },
    {
      header: "Show on Home",
      render: (row) => (row.show_on_home ? <Badge tone="success">Yes</Badge> : <Badge tone="neutral">No</Badge>),
    },
    {
      header: "",
      className: "text-right",
      render: (row) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={() => setEditing(row)}>
            Edit
          </Button>
          <Button size="sm" variant={row.is_active ? "danger" : "secondary"} onClick={() => toggleActive(row)}>
            {row.is_active ? "Deactivate" : "Activate"}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AppShell title="Room Categories">
      <p className="mb-4 text-xs text-ink-faint">
        This is the real catalog the Android app's Room Settings Category picker reads from, and the source of the
        gradient shown on that room's Home feed card — nothing here is hardcoded in the app. Deactivating a category
        removes it from the picker immediately without clearing any room that already selected it (there's no delete
        here on purpose).
      </p>
      <div className="mb-4 flex justify-end">
        <Button
          onClick={() =>
            setEditing({
              is_active: true,
              show_on_home: true,
              sort_order: 0,
              gradient_start_color: DEFAULT_START_COLOR,
              gradient_end_color: DEFAULT_END_COLOR,
            })
          }
        >
          + Add Category
        </Button>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No room categories yet"
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />

      <Modal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit Category" : "Add Category"}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={save} isLoading={isSubmitting}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label="Name" value={editing?.name ?? ""} onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))} />

          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Gradient start color"
              type="color"
              value={editing?.gradient_start_color ?? DEFAULT_START_COLOR}
              onChange={(e) => setEditing((p) => ({ ...p, gradient_start_color: e.target.value }))}
            />
            <Input
              label="Gradient end color"
              type="color"
              value={editing?.gradient_end_color ?? DEFAULT_END_COLOR}
              onChange={(e) => setEditing((p) => ({ ...p, gradient_end_color: e.target.value }))}
            />
          </div>

          <div
            className="h-12 w-full rounded-lg"
            style={{
              background: `linear-gradient(135deg, ${editing?.gradient_start_color || DEFAULT_START_COLOR}, ${
                editing?.gradient_end_color || DEFAULT_END_COLOR
              })`,
            }}
          />

          <Input
            label="Sort order"
            type="number"
            value={editing?.sort_order ?? 0}
            onChange={(e) => setEditing((p) => ({ ...p, sort_order: Number(e.target.value) }))}
          />

          {/* Round 38 (item 7) — independent of the category's own
              Active/Inactive state below the table: a category can still
              be assigned to rooms (and drive their gradient/badge) while
              hidden from Home's tab row. */}
          <Select
            label="Show on Home"
            hint="Controls whether this category appears as a tab on Home. A room can still be assigned this category even when it's hidden from Home."
            value={(editing?.show_on_home ?? true) ? "yes" : "no"}
            onChange={(e) => setEditing((p) => ({ ...p, show_on_home: e.target.value === "yes" }))}
          >
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </Select>
        </div>
      </Modal>
    </AppShell>
  );
}
